import { GroupCollectionList } from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { DateCell } from '@shared/components/data-table/cells/date-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import RatingCloud, {
  RatingCloudProps,
} from '@shared/components/ratings/rating-cloud';
import { ColumnDef } from '@tanstack/react-table';

export function useGroupCollectionsColumns(): ColumnDef<GroupCollectionList>[] {
  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => <HeaderCell column={column} name="Collection" />,
      cell: ({ getValue }) => <Text>{getValue<string>()}</Text>,
      enableColumnFilter: true,
      meta: {
        label: 'Collection',
        variant: 'text',
        placeholder: 'Search collections…',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'organizationName',
      accessorKey: 'organizationName',
      header: ({ column }) => (
        <HeaderCell column={column} name="Organisation" />
      ),
      cell: ({ getValue }) => (
        <Text variant="subtle">{getValue<string>()}</Text>
      ),
    },
    {
      id: 'modifiedTime',
      accessorKey: 'modifiedTime',
      header: ({ column }) => (
        <HeaderCell column={column} name="Last updated" />
      ),
      cell: ({ getValue }) => <DateCell date={getValue<string>()} />,
    },
    {
      id: 'numProducts',
      accessorKey: 'numProducts',
      header: ({ column }) => (
        <HeaderCell column={column} name="Products" numberColumn />
      ),
      cell: ({ getValue }) => (
        <NumberCell number={getValue<number | null>() ?? 0} rounded />
      ),
    },
    {
      id: 'averageEmissionsPerKg',
      accessorKey: 'averageEmissionsPerKg',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Avg intensity"
          numberColumn
          unit={<Unit variant="kgCO2e/kg" />}
        />
      ),
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        if (value === null)
          return <p className="pr-[18px] text-right text-gray-500">-</p>;
        return <NumberCell number={value} />;
      },
    },
    {
      id: 'averageEmissionsCategory',
      accessorKey: 'averageEmissionsCategory',
      header: ({ column }) => <HeaderCell column={column} name="Rating" />,
      cell: ({ getValue }) => {
        const rating = getValue<RatingCloudProps['rating'] | ''>();
        if (!rating) return null;
        return <RatingCloud rating={rating} />;
      },
      enableSorting: false,
    },
  ];
}
